import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'react-toastify';
import { FaStar, FaTimes, FaSpinner } from 'react-icons/fa';
import * as Sentry from '@sentry/react';

/**
 * CsatSurveyModal
 *
 * Shows a satisfaction survey modal for the ticket requester when a ticket
 * is solved/closed. Fetches pending survey from backend and allows the user
 * to submit a 1-5 star rating with optional feedback.
 *
 * Only renders when a pending (unsubmitted) survey exists for the action item.
 */
const CsatSurveyModal = ({ actionItemId, ticketTitle }) => {
    const { apiFetch } = useAuth();
    const [survey, setSurvey] = useState(null);
    const [visible, setVisible] = useState(false);
    const [rating, setRating] = useState(0);
    const [hoverRating, setHoverRating] = useState(0);
    const [feedback, setFeedback] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [dismissed, setDismissed] = useState(false);

    const loadSurvey = useCallback(async () => {
        try {
            const res = await apiFetch(`/api/ITServiceDesk/csat/${actionItemId}`);
            if (res.ok) {
                const json = await res.json();
                if (json.success && json.data && !json.data.isSubmitted) {
                    setSurvey(json.data);
                    setVisible(true);
                }
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            // Silent — survey is optional
        }
    }, [apiFetch, actionItemId]);

    useEffect(() => { (async () => { await loadSurvey(); })(); }, [loadSurvey]);

    const handleSubmit = async () => {
        if (rating === 0) {
            toast.error('Please select a rating');
            return;
        }
        setSubmitting(true);
        try {
            const res = await apiFetch(`/api/ITServiceDesk/csat/${actionItemId}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ rating, feedback })
            });
            if (res.ok) {
                toast.success('Thank you for your feedback!');
                setVisible(false);
            } else {
                toast.error('Failed to submit survey');
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('Failed to submit survey');
        } finally {
            setSubmitting(false);
        }
    };

    if (!visible || dismissed || !survey) return null;

    return (
        <div className="csat-modal-overlay" onClick={() => setDismissed(true)}>
            <div className="csat-modal" onClick={e => e.stopPropagation()}>
                <div className="csat-modal-header">
                    <h3>How was your experience?</h3>
                    <button className="csat-close-btn" onClick={() => setDismissed(true)}>
                        <FaTimes />
                    </button>
                </div>
                <div className="csat-modal-body">
                    <p className="csat-ticket-ref">
                        Ticket: <strong>{ticketTitle}</strong>
                    </p>
                    <div className="csat-stars">
                        {[1, 2, 3, 4, 5].map(star => (
                            <FaStar
                                key={star}
                                className={`csat-star ${star <= (hoverRating || rating) ? 'filled' : 'empty'}`}
                                onMouseEnter={() => setHoverRating(star)}
                                onMouseLeave={() => setHoverRating(0)}
                                onClick={() => setRating(star)}
                            />
                        ))}
                    </div>
                    <div className="csat-labels">
                        <span>Very Dissatisfied</span>
                        <span>Very Satisfied</span>
                    </div>
                    <textarea
                        className="csat-feedback"
                        value={feedback}
                        onChange={e => setFeedback(e.target.value)}
                        placeholder="Any additional feedback? (optional)"
                        rows={3}
                        maxLength={2000}
                    />
                </div>
                <div className="csat-modal-footer">
                    <button className="csat-dismiss-btn" onClick={() => setDismissed(true)}>
                        Skip
                    </button>
                    <button
                        className="csat-submit-btn"
                        onClick={handleSubmit}
                        disabled={submitting || rating === 0}
                    >
                        {submitting ? <><FaSpinner className="spin" /> Submitting...</> : 'Submit'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CsatSurveyModal;
